import { useId, useRef, useState } from 'react'

import { FxAlert, FxButton, FxTextField } from '@/components/fx'
import { copyToClipboard } from '@/lib/utils'
import { SettingsDialog } from './SettingsDialog'
import { apiKeyErrorMessage } from './apiKeys'
import { useCreateApiKey } from './useApiKeys'

/**
 * Creating a key, in two steps in one dialog: the name, then the secret.
 *
 * The second step is the whole security property of the flow. The gateway
 * stores only the key's hash, so the token in `createdToken` is the only copy
 * that will ever exist. Three things follow, and each is deliberate:
 *
 * 1. The token is held in this component's own state, not read out of the
 *    mutation's `data` — a `reset()`, a retry or a re-mount of the mutation
 *    would blank `data` and the secret with it.
 * 2. The dialog stops being dismissable once it is shown: no Escape, no click
 *    on the backdrop, no close button. Leaving is a deliberate press of
 *    "Fertig", which is also what tells the list to refetch.
 * 3. The field is a real focusable, selectable `<input readonly>`, so the
 *    token can be copied by hand on a browser that refuses the clipboard API
 *    (a non-secure origin, a denied permission).
 */
export function CreateApiKeyDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called once, when the dialog is left — the moment it is safe to refetch. */
  onCreated: () => void
}) {
  const formId = useId()
  const tokenId = useId()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [createdToken, setCreatedToken] = useState<string | null>(null)
  const [copy, setCopy] = useState<'idle' | 'done' | 'failed'>('idle')
  const [error, setError] = useState<string | null>(null)
  const created = useRef(false)

  const { mutate, isPending } = useCreateApiKey()

  function close() {
    setName('')
    setDescription('')
    setCreatedToken(null)
    setCopy('idle')
    setError(null)
    onOpenChange(false)
    if (created.current) {
      created.current = false
      onCreated()
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Bitte geben Sie einen Namen an, damit Sie den Schlüssel später wiedererkennen.')
      return
    }
    setError(null)
    mutate(
      { name: trimmed, description: description.trim() || undefined },
      {
        onSuccess: (key) => {
          created.current = true
          setCreatedToken(key.token)
        },
        onError: (cause) =>
          setError(apiKeyErrorMessage(cause, 'Der Schlüssel konnte nicht angelegt werden.')),
      },
    )
  }

  async function onCopy() {
    if (!createdToken) return
    setCopy((await copyToClipboard(createdToken)) ? 'done' : 'failed')
  }

  if (createdToken) {
    return (
      <SettingsDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close()
        }}
        dismissable={false}
        title="Ihr neuer Schlüssel"
        description="Kopieren Sie ihn jetzt. Wir speichern nur seine Prüfsumme und können ihn Ihnen später nicht noch einmal zeigen."
        footer={
          <FxButton onClick={close}>Fertig, ich habe den Schlüssel gespeichert</FxButton>
        }
      >
        <div className="flex flex-col gap-2">
          <label htmlFor={tokenId} className="text-body-strong text-text">
            Schlüssel
          </label>
          <div className="flex items-end gap-4">
            <input
              id={tokenId}
              readOnly
              value={createdToken}
              // Selected on focus so "markieren und kopieren" is one keystroke
              // away where the clipboard API is unavailable.
              onFocus={(event) => event.currentTarget.select()}
              className="h-control w-full rounded-md bg-ground px-4 font-mono text-small text-text shadow-raised"
            />
            <FxButton variant="outlined" onClick={onCopy} icon="content_copy">
              Kopieren
            </FxButton>
          </div>
          <p role="status" className="min-h-[1.45em] text-small text-text-muted">
            {copy === 'done' && 'In die Zwischenablage kopiert.'}
            {copy === 'failed' &&
              'Das Kopieren hat dieser Browser nicht zugelassen. Bitte markieren Sie den Schlüssel und kopieren Sie ihn von Hand.'}
          </p>
        </div>

        <FxAlert tone="warning" title="Nur jetzt sichtbar">
          <p>
            Bewahren Sie den Schlüssel so auf wie ein Passwort. Wer ihn hat, kann über die API
            alles, was Sie können. Ist er verloren, legen Sie einen neuen an und widerrufen diesen.
          </p>
        </FxAlert>
      </SettingsDialog>
    )
  }

  return (
    <SettingsDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title="Neuen Schlüssel anlegen"
      description="Der Schlüssel gehört zu Ihrem Konto und kann alles, was Sie können."
      footer={
        <>
          <FxButton variant="outlined" onClick={close} disabled={isPending}>
            Abbrechen
          </FxButton>
          <FxButton type="submit" form={formId} disabled={isPending}>
            {isPending ? 'Wird angelegt …' : 'Schlüssel anlegen'}
          </FxButton>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="flex flex-col gap-5">
        <FxTextField
          label="Name"
          required
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Import aus dem CRM"
          hint="Woran Sie diesen Schlüssel in der Liste wiedererkennen."
          disabled={isPending}
        />
        <FxTextField
          label="Beschreibung"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Nächtlicher Export in das Warenwirtschaftssystem"
          disabled={isPending}
        />
        {error && (
          <FxAlert tone="danger">
            <p>{error}</p>
          </FxAlert>
        )}
      </form>
    </SettingsDialog>
  )
}
